import type { EvidenceSourceType, InsightEvidenceGroup, InsightEvidenceItem } from "~vue/api/generated/intelligence-evidence";
import { COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS } from "~vue/components/navigation/menuPanes";
import { i18n } from "~vue/i18n";
import type { LabInsightLocation } from "~vue/types/labs";

export type { EvidenceSourceType, InsightEvidenceGroup, InsightEvidenceItem };

export type InsightEvidenceAnalyticsContext = {
    insightId: string;
    experimentSlug: string;
    location: LabInsightLocation;
};

const EVIDENCE_SOURCE_WEIGHTS: Record<EvidenceSourceType, number> = {
    collaboration_profile: 1,
    past_chat: 2,
    user_memory: 2,
    user_profile_note: 2,
    connection_note: 3,
    lab_insight: 4,
    perspective: 5,
    pair_perspective: 5,
    calendar_event: 6,
    granola_meeting: 7,
    workiq_outlook: 7,
    workiq_teams: 7,
    workiq_calendar: 7,
    workiq_documents: 7,
    workiq_microsoft_365: 7,
    other: 7,
    performance_review_conversation: 8,
    performance_review_upload: 8,
};

export type EvidenceSourceCounts = Partial<Record<EvidenceSourceType, number>>;

export function sortEvidenceGroups(groups: InsightEvidenceGroup[]): InsightEvidenceGroup[] {
    return [...groups].sort((left, right) => EVIDENCE_SOURCE_WEIGHTS[left.kind] - EVIDENCE_SOURCE_WEIGHTS[right.kind]);
}

export function evidenceSourceCounts(groups: InsightEvidenceGroup[]): EvidenceSourceCounts {
    return groups.reduce<EvidenceSourceCounts>((counts, group) => {
        counts[group.kind] = (counts[group.kind] ?? 0) + group.count;
        return counts;
    }, {});
}

/** Perspective sources use the PerspectiveCircles mark in EvidenceSourceIcon. */
export const EVIDENCE_ICON_CLASSES: Record<Exclude<EvidenceSourceType, "perspective" | "pair_perspective">, string> = {
    calendar_event: "bi-calendar3",
    granola_meeting: "bi-journal-text",
    workiq_outlook: "bi-envelope",
    workiq_teams: "bi-people-fill",
    workiq_calendar: "bi-calendar-event",
    workiq_documents: "bi-file-earmark-text",
    workiq_microsoft_365: "bi-microsoft",
    past_chat: "bi-chat-left-text",
    user_memory: "bi-journal-bookmark",
    user_profile_note: "bi-journal-text",
    connection_note: "bi-person-lines-fill",
    collaboration_profile: COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS,
    lab_insight: "bi-lightbulb",
    performance_review_conversation: "bi-clipboard-data",
    performance_review_upload: "bi-clipboard-data",
    other: "bi-link-45deg",
};

export function evidenceChipLabel(group: InsightEvidenceGroup): string {
    if (group.kind === "user_profile_note") {
        return i18n.global.t("labs.evidence.profileNotes", { count: group.count });
    }
    return group.chip_label ?? group.label;
}

/** "N noun(s)" phrase for the redesigned "Based on: 2 calendar events" inline-text treatment. */
const EVIDENCE_LINK_TEXT: Record<EvidenceSourceType, (count: number) => string> = {
    calendar_event: (count) => i18n.global.t("insights.evidence.link.calendarEvent", { count }, count),
    granola_meeting: (count) => i18n.global.t("insights.evidence.link.granolaMeeting", { count }, count),
    workiq_outlook: (count) => i18n.global.t("insights.evidence.link.workiqOutlook", { count }, count),
    workiq_teams: (count) => i18n.global.t("insights.evidence.link.workiqTeams", { count }, count),
    workiq_calendar: (count) => i18n.global.t("insights.evidence.link.workiqCalendar", { count }, count),
    workiq_documents: (count) => i18n.global.t("insights.evidence.link.workiqDocuments", { count }, count),
    workiq_microsoft_365: (count) => i18n.global.t("insights.evidence.link.workiqMicrosoft365", { count }, count),
    past_chat: (count) => i18n.global.t("insights.evidence.link.pastChat", { count }, count),
    perspective: (count) => i18n.global.t("insights.evidence.link.perspective", { count }, count),
    pair_perspective: (count) => i18n.global.t("insights.evidence.link.pairPerspective", { count }, count),
    connection_note: (count) => i18n.global.t("insights.evidence.link.connectionNote", { count }, count),
    collaboration_profile: (count) => i18n.global.t("insights.evidence.link.collaborationProfile", { count }, count),
    performance_review_conversation: (count) => i18n.global.t("insights.evidence.link.performanceReview", { count }, count),
    performance_review_upload: (count) => i18n.global.t("insights.evidence.link.performanceReview", { count }, count),
    lab_insight: (count) => i18n.global.t("insights.evidence.link.labInsight", { count }, count),
    user_memory: (count) => i18n.global.t("insights.evidence.link.userMemory", { count }, count),
    user_profile_note: (count) => i18n.global.t("labs.evidence.profileNotes", { count }, count),
    other: (count) => i18n.global.t("insights.evidence.link.other", { count }, count),
};

/**
 * The chip's bare-digit badge ("Calendar 2") reads fine next to an icon, but sits oddly inline
 * in a sentence ("...and Calendar2"). `chip_label` already spells out the self/named-person case
 * (always count === 1, see `_collaboration_profile_chip_label`), so only the generic fallback
 * needs a "N noun(s)" phrase built here.
 */
export function evidenceLinkText(group: InsightEvidenceGroup): string {
    if (group.kind === "user_profile_note") {
        return evidenceChipLabel(group);
    }
    if (group.chip_label) {
        return group.chip_label;
    }
    return EVIDENCE_LINK_TEXT[group.kind](group.count);
}

export function calculateVisibleEvidenceGroupCount(containerWidth: number, labelWidth: number, groupWidths: number[], overflowWidths: number[], gap = 8): number {
    const labelGapCount = labelWidth > 0 ? 1 : 0;
    const allGroupsWidth = labelWidth + groupWidths.reduce((total, width) => total + width, 0) + gap * Math.max(groupWidths.length + labelGapCount - 1, 0);
    if (allGroupsWidth <= containerWidth) {
        return groupWidths.length;
    }

    let visibleWidth = labelWidth;
    let visibleGroups = 0;
    for (let visibleCount = 0; visibleCount < groupWidths.length; visibleCount += 1) {
        const hiddenCount = groupWidths.length - visibleCount;
        const overflowWidth = overflowWidths[hiddenCount - 1] ?? 0;
        const requiredWidth = visibleWidth + overflowWidth + gap * (visibleCount + labelGapCount);
        if (requiredWidth > containerWidth) {
            break;
        }
        visibleGroups = visibleCount;
        visibleWidth += groupWidths[visibleCount] ?? 0;
    }

    return visibleGroups;
}
